import { L } from 'harmony-language';
import React, { useMemo } from 'react';
import { OrgQueryKeys } from '../../../../../api/config';
import { useOrganizationQuery } from '../../../../../api/queries/use-organization-query';
import type { LocationGroup, OrganizationLocation, Stop } from '../../../../../types';
import { toSiteDisplayNameFromOrganizationLocation } from '../../../../../utils/data-mapping';
import { isValidStopLocation } from '../../../../../utils/validation-utils';
import { AgisticsSelect } from '../../../../shared/agistics-select';
import { useDraftLoadStopContext } from '../../../hooks/draft-load-stop-context';
import { getSingleColorTagStyle } from '../../../utils/drafts-loads-utils';
import { useEditableStopLocationTravelTimes } from './use-editable-stop-location-travel-times';

interface EditableStopLocationProps {
    stop: Stop;
}

export const EditableStopLocation: React.FC<EditableStopLocationProps> = (props) => {
    const { stop } = props;
    const { onEditedStopChanged, isCarrier, isComplete } = useDraftLoadStopContext();
    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: locationGroups = [] } = useOrganizationQuery<LocationGroup[]>(OrgQueryKeys.locationGroups);
    useEditableStopLocationTravelTimes(stop);

    const organizationLocationItems = useMemo(() => {
        return organizationLocations.map(organizationLocation => ({
            id: organizationLocation.id,
            value: toSiteDisplayNameFromOrganizationLocation(organizationLocation),
        }));
    }, [organizationLocations]);

    function getStyleForLocation(locationId: number) {
        const orgLocation = organizationLocations.find(loc => loc.id === locationId);
        return getSingleColorTagStyle(orgLocation?.groupId || null, locationGroups);
    }

    return (
        <AgisticsSelect
            item={stop.organizationLocationId}
            items={organizationLocationItems}
            onChange={(organizationLocationId) => {
                onEditedStopChanged({
                    organizationLocationId: organizationLocationId,
                    organizationSubLocationIds: null,
                });
            }}
            // add / spit stop unless we default that differently
            showNone={true}
            confinedSpace={true}
            customPopper={true}
            disabled={isCarrier || isComplete}
            validations={[{
                isValid: () => isValidStopLocation(stop.organizationLocationId),
                message: L.invalidLocation(),
            }]}
            styleForOptionsFn={getStyleForLocation}
        />
    );
};
